/**
 * Configured countries + non-Arabic → language selection modal.
 * Custom dropdown (avoids iOS native <select> misposition in fixed modals).
 * Hidden localization select syncs value; Confirm submits the form.
 */
class RegionalLocalePrompt extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._storageKey = this.dataset.storageKey || 'tp:regional-locale-prompt-dismissed';
    this._delay = Number.parseInt(this.dataset.delay || '200', 10);
    this._form = this.querySelector('form');
    this._select = this.querySelector('[data-regional-locale-select]');
    this._dropdown = this.querySelector('[data-regional-locale-dropdown]');
    this._trigger = this.querySelector('[data-regional-locale-trigger]');
    this._listbox = this.querySelector('[data-regional-locale-listbox]');
    this._label = this.querySelector('[data-regional-locale-label]');
    this._options = this.querySelectorAll('[data-regional-locale-option]');
    this._closeButtons = this.querySelectorAll('[data-regional-locale-dismiss]');
    this._initialLocale = this._select?.value || '';
    this._isOpen = false;

    this._onClose = this._onClose.bind(this);
    this._onConfirm = this._onConfirm.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onOptionClick = this._onOptionClick.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);

    if (this._isDismissed()) {
      this.remove();
      return;
    }

    this._closeButtons.forEach((button) => {
      button.addEventListener('click', this._onClose);
    });
    this._form?.addEventListener('submit', this._onConfirm);
    this._trigger?.addEventListener('click', this._onTriggerClick);
    this._options.forEach((option) => {
      option.addEventListener('click', this._onOptionClick);
    });
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onDocumentClick);

    window.setTimeout(() => {
      if (this._isDismissed()) {
        this.remove();
        return;
      }

      this.hidden = false;
      this.offsetHeight;
      this.classList.add('is-visible');
      document.documentElement.classList.add('regional-locale-prompt-open');
    }, Number.isFinite(this._delay) ? this._delay : 200);
  }

  disconnectedCallback() {
    this._closeButtons?.forEach((button) => {
      button.removeEventListener('click', this._onClose);
    });
    this._form?.removeEventListener('submit', this._onConfirm);
    this._trigger?.removeEventListener('click', this._onTriggerClick);
    this._options?.forEach((option) => {
      option.removeEventListener('click', this._onOptionClick);
    });
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onDocumentClick);
    document.documentElement.classList.remove('regional-locale-prompt-open');
  }

  _isDismissed() {
    try {
      return sessionStorage.getItem(this._storageKey) === '1';
    } catch (e) {
      return false;
    }
  }

  _setDropdownOpen(open) {
    this._isOpen = Boolean(open);
    this._dropdown?.classList.toggle('is-open', this._isOpen);
    this._trigger?.setAttribute('aria-expanded', this._isOpen ? 'true' : 'false');

    if (this._listbox) {
      this._listbox.hidden = !this._isOpen;
    }
  }

  _onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this._setDropdownOpen(!this._isOpen);
  }

  _onOptionClick(event) {
    const option = event.currentTarget;
    const value = option?.dataset?.value;
    const label = option?.dataset?.label;
    if (!value || !this._select) return;

    this._select.value = value;

    if (this._label && label) {
      this._label.textContent = label;
    }

    this._options.forEach((item) => {
      const selected = item === option;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    this._setDropdownOpen(false);
  }

  _onDocumentClick(event) {
    if (!this._isOpen) return;
    if (this._dropdown?.contains(event.target)) return;
    this._setDropdownOpen(false);
  }

  _onKeydown(event) {
    if (!this.classList.contains('is-visible')) return;

    if (event.key === 'Escape') {
      if (this._isOpen) {
        event.preventDefault();
        this._setDropdownOpen(false);
        this._trigger?.focus();
        return;
      }

      this._onClose(event);
    }
  }

  _onClose(event) {
    try {
      event?.preventDefault();
      sessionStorage.setItem(this._storageKey, '1');

      this._setDropdownOpen(false);
      this.classList.remove('is-visible');
      document.documentElement.classList.remove('regional-locale-prompt-open');
      this.hidden = true;
    } catch (e) {
      // Private mode may block sessionStorage
    }
  }

  _onConfirm(event) {
    if (!this._select || !this._form) return;

    if (this._select.value === this._initialLocale) {
      event.preventDefault();
      this._onClose(event);
      return;
    }

    try {
      sessionStorage.setItem(this._storageKey, '1');
    } catch (e) {
      // Private mode may block sessionStorage
    }
  }
}

if (!window.customElements.get('regional-locale-prompt')) {
  window.customElements.define('regional-locale-prompt', RegionalLocalePrompt);
}
